import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { BRL, ScopeToggle } from "./shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = ["Salário", "Vendas", "Alimentação", "Transporte", "Moradia", "Lazer", "Serviços", "Saúde", "Educação", "Impostos", "Outros"];
const empty = (scope) => ({ scope, type: "expense", category: "Outros", description: "", amount: "", date: new Date().toISOString().slice(0, 10) });

export default function Transactions({ scope, setScope }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty(scope));
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    api.get(`/transactions?scope=${scope}`).then(({ data }) => setItems(data));
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty(scope)); setEditId(null); setOpen(true); };
  const openEdit = (t) => { setForm({ ...t, amount: String(t.amount) }); setEditId(t.id); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, scope, amount: parseFloat(form.amount) };
      if (!payload.amount || payload.amount <= 0) return toast.error("Informe um valor válido");
      if (editId) await api.put(`/transactions/${editId}`, payload);
      else await api.post("/transactions", payload);
      toast.success(editId ? "Transação atualizada" : "Transação adicionada");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/transactions/${id}`); toast.success("Transação excluída"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8" data-testid="transactions-section">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-text-primary">Transações</h2>
          <p className="text-text-secondary font-body mt-1">Gerencie suas entradas e saídas.</p>
        </div>
        <div className="flex items-center gap-3">
          <ScopeToggle scope={scope} setScope={setScope} />
          <Button data-testid="add-transaction-btn" onClick={openNew} className="rounded-md bg-primary hover:bg-primary/90">
            <Plus className="h-4 w-4 mr-1" /> Nova
          </Button>
        </div>
      </div>

      <div className="border border-stone-200 bg-white rounded-md overflow-hidden">
        {items.length === 0 && <p className="p-8 text-center text-text-muted font-body">Nenhuma transação registrada.</p>}
        {items.map((t) => (
          <div key={t.id} data-testid={`tx-row-${t.id}`}
            className="flex items-center gap-4 px-6 py-4 border-b border-stone-100 last:border-0 hover:bg-stone-50 transition-colors">
            {t.type === "income"
              ? <ArrowUpCircle className="h-8 w-8 text-primary shrink-0" />
              : <ArrowDownCircle className="h-8 w-8 text-secondary shrink-0" />}
            <div className="min-w-0 flex-1">
              <p className="font-body font-medium text-text-primary truncate">{t.description || t.category}</p>
              <p className="text-sm text-text-muted font-body">{t.category} · {t.date}{t.imported ? " · importado" : ""}</p>
            </div>
            <p className={`font-heading font-bold ${t.type === "income" ? "text-primary" : "text-secondary"}`}>
              {t.type === "income" ? "+" : "-"}{BRL(t.amount)}
            </p>
            <div className="flex gap-1">
              <button data-testid={`edit-${t.id}`} onClick={() => openEdit(t)} className="p-2 rounded hover:bg-stone-200 text-text-secondary">
                <Pencil className="h-4 w-4" />
              </button>
              <button data-testid={`delete-${t.id}`} onClick={() => remove(t.id)} className="p-2 rounded hover:bg-red-50 text-secondary">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-md" data-testid="transaction-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Editar" : "Nova"} transação</DialogTitle>
            <DialogDescription className="font-body">Preencha os dados da transação {scope === "business" ? "empresarial" : "pessoal"}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="font-body">Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="tx-type" className="rounded-md"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Receita</SelectItem>
                    <SelectItem value="expense">Despesa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-body">Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="tx-category" className="rounded-md"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-body">Descrição</Label>
              <Input data-testid="tx-description" className="rounded-md" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ex: Supermercado" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="font-body">Valor (R$)</Label>
                <Input data-testid="tx-amount" type="number" step="0.01" className="rounded-md" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0,00" />
              </div>
              <div className="space-y-2">
                <Label className="font-body">Data</Label>
                <Input data-testid="tx-date" type="date" className="rounded-md" value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-transaction" onClick={save} className="rounded-md bg-primary hover:bg-primary/90">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
